import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import { useQuery, useMutation } from "react-apollo";
import {
  Badge,
  Button,
  Caption,
  Card,
  ChoiceList,
  Collapsible,
  Checkbox,
  Layout,
  Link,
  Modal,
  ResourceItem,
  Spinner,
  Stack,
  Tag,
  TextContainer,
  TextStyle,
  Thumbnail,
  Tooltip,
} from "@shopify/polaris";

import {
  CircleCancelMinor,
  CircleTickOutlineMinor,
  ExportMinor,
  ImportMinor,
  MarkFulfilledMinor,
  ViewMajor,
  WholesaleMajor,
} from "@shopify/polaris-icons";

import { QuickReport, QuickAction } from "app/reports";
import { SingleColumnLayout } from "layout/private/components";

// import from lib
import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";
import UpgradAppPermission from "app/appPermission/modules/UpgradAppPermission";

import { Banner, ModalPopup, PermissionBanner, ResourceList, SkeletonList, Toast } from "lib/components";
import { baseHelper, errorHelper, imageHelper, storageHelper, statusHelper } from "lib/helpers";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";
import { ProductCard, ProductSubCard, StyledOrderVersioning } from "asset/styles/globalStyle";

import { GET_ORDER_LIST } from "app/orders/apollo/queries";
import { GET_FULFILLMENT_TRACKING_DETAILS_SETTING } from "app/setup/apollo/queries";
import { GET_PRODUCT_LISTING } from "app/productOld/apollo/queries";
import { BULK_ACCEPT, BULK_REJECT, UPDATE_ORDER_LINE_DELIVERY_STATUS } from "app/orders/apollo/mutations";
import { updateOrderLineTracking } from "app/orders/apollo/updateOrderNote";
import MoreFilters from "app/orders/modules/generic/moreFilters/moreFilter";
import Popover from "lib/components/popover/popover";
import ORDER_LIST from "app/orders/apollo/subscriptions/orders";
import listData from "app/orders/modules/operator/features/list/orderListData";
import SyncOrder from "app/orders/modules/operator/features/sync/syncOrder";
import OperatorExportOrder from "app/orders/modules/operator/features/export/index";
import OrderVersioning from "app/orders/modules/generic/orderVersioning/orderVersioning";
import LineItem from "app/orders/modules/operator/features/list/component/lineItem/lineItem";

import OperatorLineReturn from "app/orders/modules/operator/features/lineReturn/index";
import FulfillOrder from "../fulfill/fulfill";

const OperatorOrderList = () => {
  const { history } = useContext(PrivateContext);
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });
  const {
    ACCEPT,
    ACCEPTED,
    ACCEPT_LABEL,
    ACKNOWLEDGE,
    CLOSED,
    COLLECT,
    COLLECTED,
    CREATE,
    DROPSHIP,
    FULFILLED,
    gql,
    HIDE_CAPITAL,
    PARTIAL,
    PARTIAL_COLLECT,
    PARTIALLY_COMPLETE,
    PARTIALLY_FULFILLED,
    PARTIAL_REFUND,
    REFUND,
    REJECT,
    RESERVE,
    REJECT_LABEL,
    SHIPPED,
    SHOW,
  } = constant;
  const { cms, currentUser, isShopifyCustomOperator } = useContext(PrivateContext);
  const {
    fulfillmentScopeExists = false,
    roles: { name },
    moneyFormat,
  } = currentUser;
  const { mainLink, filterByStatusOption } = listData(cms);
  const queryParam = baseHelper.getQueryParams(history.location.search);
  const [perPage, setPerPage] = useState(parseInt(queryParam.perPage, 10) || 10);
  const [page, setPage] = useState(parseInt(queryParam.page, 10) || 1);
  const [search, setSearch] = useState(queryParam.search || "");
  const selectedSort = queryParam.sort_name && `${queryParam.sort_name}_${queryParam.sort_order}`;
  const paramListSearch = (queryParam.list_search && queryParam.list_search.split(",")) || [];
  const [selectedOptions, setSelectedOptions] = useState(paramListSearch || []);
  const [sortValue, setSortValue] = useState(selectedSort || constant.CREATED_DESC);
  const [taggedWith, setTaggedWith] = useState(queryParam.list_search || "");
  const [selectedFilter, setSelectedFilter] = useState(queryParam.list_filter || "");
  const paramFilter = (queryParam.filter && queryParam.filter.split(",")) || [];
  const [filterByStatus, setFilterByStatus] = useState(paramFilter || []);

  const [checked, setChecked] = useState(false);
  const [disable, setDisable] = useState(false);
  const [isExportModal, setIsExportModal] = useState(false);
  const [isSyncModal, setIsSyncModal] = useState(false);
  const [isFulfillModal, setIsFulfillModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rejectModel, setRejectModal] = useState(false);

  const [initialTrackingCompany, setInitialTrackingCompany] = useState("");
  const [initialTrackingNumber, setInitialTrackingNumber] = useState("");
  const [message, setMessage] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [orderList, setOrderList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [tagInputValue, setTagInputValue] = useState([]);
  const [productList, setProductList] = useState([]);
  const [orderCount, setOrderCount] = useState(0);
  const [selectedTab, setSelectedTab] = useState(0);

  const [isOrderId, setIsOrderId] = useState();
  const [isOrderReject, setIsOrderReject] = useState();
  const [getOrderId, setGetOrderId] = useState(false);
  const [totalOrderItem, setTotalOrderItem] = useState();
  const [totalOrderVendor, setTotalOrderVendor] = useState();

  const [active, setActive] = useState({});
  const [activePopover, setActivePopover] = useState({});
  const [expanded, setExpanded] = useState({});
  const [trackingModal, setTrackingModal] = useState({});
  const [popoverDisable, setPopoverDisable] = useState(false);
  const [deliveryOrderId, setDeliveryOrderId] = useState("");
  const [bulkToggle, setBulkToggle] = useState({});
  const [historyActive, setHistoryActive] = useState({});
  const [productTag, setProductTag] = useState();
  const [isProductList, setIsProductList] = useState(false);
  const [appUpdate, setAppUpdate] = useState(storageHelper.get("customAppUpdate"));
  const [isEnabledData, setIsEnabledData] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = useCallback(() => {
    setRejectModal(!rejectModel);
    setChecked(false);
  }, [rejectModel]);

  const [initiateReturn, setInitiateReturn] = useState({
    showModal: false,
    selectedOrder: false,
  });

  const inputData = {
    filter: filterByStatus && filterByStatus.length ? filterByStatus : queryParam.filter,
    path: queryParam.path,
    sort_name: "shopifyOrderDate",
    sort_order: queryParam.sort_order || "desc",
    sort_date: queryParam.sort_date,
    page: parseInt(queryParam.page, 10) || 1,
    perPage: parseInt(queryParam.perPage, 10) || 10,
    list_search: taggedWith ? queryParam.list_search : tagInputValue || "",
    list_filter: queryParam.list_filter,
    search: queryParam.search,
  };
  const { data: order, loading: orderLoading, error: orderError, subscribeToMore, refetch } = useQuery(GET_ORDER_LIST, {
    variables: {
      input: inputData,
    },
  });

  const [updateOrderLine, { data: dataUpdateOrderLineTracking, loading: loadingOrderLineTracking }] = useMutation(
    updateOrderLineTracking
  );

  // eslint-disable-next-line no-unused-vars
  const [bulkAccept, { loading: loadingBulkAccept }] = useMutation(BULK_ACCEPT, {
    refetchQueries: [
      {
        query: GET_ORDER_LIST,
        variables: {
          input: inputData,
        },
      },
    ],
  });

  // eslint-disable-next-line no-unused-vars
  const [bulkReject, { loading: loadingBulkReject }] = useMutation(BULK_REJECT, {
    refetchQueries: [
      {
        query: GET_ORDER_LIST,
        variables: {
          input: inputData,
        },
      },
    ],
  });

  // eslint-disable-next-line no-unused-vars
  const [updateOrderDelivery, { loading: loadingUpdateOrderDelivery }] = useMutation(
    UPDATE_ORDER_LINE_DELIVERY_STATUS,
    {
      refetchQueries: [
        {
          query: GET_ORDER_LIST,
          variables: {
            input: inputData,
          },
        },
      ],
    }
  );

  const { loading: loadingProductListing, data: dataProductList } = useQuery(GET_PRODUCT_LISTING);

  const { data: trackingData, loading: fulfillSettingLoading } = useQuery(GET_FULFILLMENT_TRACKING_DETAILS_SETTING);

  useEffect(() => {
    setTimeout(() => {
      setMessage("");
    }, 2000);
  }, [message]);

  const linkData = [
    {
      action: () => setIsSyncModal(true),
      caption: "Import all orders from your Shopify store",
      id: "importOrder",
      primary: true,
      label: cms("section.orderList.label.importOrderFromStore"),
      icon: ImportMinor,
    },
    {
      action: () => setIsExportModal(true),
      caption: "Export your orders",
      id: "exportOrder",
      label: cms("section.orderList.label.exportOrder"),
      icon: ExportMinor,
    },
  ];

  if (isProductList) {
    linkData.push({
      action: () => history.push("/orders/bulk"),
      caption: "Bulk Fulfill Orders",
      id: "fulfillOrder",
      primary: true,
      label: "Bulk Fulfill Orders",
      icon: ExportMinor,
    });
  }

  useEffect(() => {
    if (!orderLoading) {
      setLoading(false);
    }
  }, [orderLoading]);

  useEffect(() => {
    if (orderError) {
      setBanner({
        isOpen: true,
        status: "critical",
        title: errorHelper.parse(orderError),
      });
    }
  }, [orderError]);

  useEffect(() => {
    if (dataProductList) {
      const productistResponse = baseHelper.getResponseData(dataProductList, "getProductListingSetting");
      if (productistResponse) {
        const { isEnabled = false } = productistResponse;
        setIsProductList(isEnabled);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProductList]);

  const responseError = baseHelper.getResponseError(order, gql.GET_ORDER_LIST);
  useEffect(() => {
    if (responseError) {
      setBanner({
        isOpen: true,
        status: "critical",
        title: responseError,
      });
    }
  }, [responseError]);
  const responseData = baseHelper.getResponseData(order, gql.GET_ORDER_LIST);
  useEffect(() => {
    if (responseData) {
      setOrderList(responseData && responseData.orderList);
      setOrderCount(responseData && responseData.count);
    }
  }, [responseData]);

  useEffect(() => {
    if (filterByStatus && filterByStatus.length) {
      baseHelper.setUrl(history, { filter: filterByStatus, page: 1 });
    }
    if (selectedOptions && selectedOptions.length) {
      baseHelper.setUrl(history, { list_search: selectedOptions, page: 1 });
    }
    return baseHelper.setUrl(history, {
      filter: "",
      list_search: "",
      list_filter: selectedOptions && selectedOptions.length ? constant.PRODUCT : "",
      page: 1,
    });
  }, [filterByStatus, history, selectedOptions]);

  useEffect(() => {
    if (trackingData) {
      const resData = baseHelper.getResponseData(trackingData, "getFulfillmentTrackingDetailsSetting");
      const resError = baseHelper.getResponseError(trackingData, "getFulfillmentTrackingDetailsSetting");
      if (resError) {
        setBanner({
          isOpen: true,
          status: constant.CRITICAL,
          title: resError,
        });
        return;
      }
      if (resData) {
        const { isEnabled } = resData || {};
        setIsEnabledData(isEnabled);
      }
    }
  }, [trackingData, setBanner, "getFulfillmentTrackingDetailsSetting"]);

  const removeTag = useCallback(
    (tag, key) => () => {
      if (key === constant.PRODUCT) {
        setSelectedOptions((previousTags) => previousTags.filter((previousTag) => previousTag !== tag));
        return true;
      }
      setFilterByStatus((previousTags) => previousTags.filter((previousTag) => previousTag !== tag));
      return true;
    },
    []
  );

  /* const setFilters = useCallback(
    (newFilter, select) => {
      const getFilter = baseHelper.setQueryParams(history.location, { filter: newFilter, tab: select });
      history.push(`${history.location.pathname}?${getFilter}`);
    },
    [history]
  ); */

  const itemCollapseAction = (id) => {
    setActive((prev) => {
      return {
        ...prev,
        [id]: !active[id],
      };
    });
    setExpanded((prev) => {
      return {
        ...prev,
        [id]: !expanded[id],
      };
    });
  };

  const handleSearchChange = (searchValue) => {
    setSearch(searchValue);
    baseHelper.setUrl(history, { search: searchValue.trim(), page: 1 });
  };
  const handleSort = (selected) => {
    setSortValue(selected);
    const sort = selected.split("_");
    if (sort && sort.length === 2) {
      baseHelper.setUrl(history, { sort_name: sort[0], sort_order: sort[1] });
    }
  };
  const handleQueryClear = () => {
    setSearch("");
    baseHelper.setUrl(history, { search: "" });
  };

  const handleTaggedWithRemove = (clearFilter = false, key) => {
    const clearParams = key === constant.FILTER_BY_STATUS ? { filter: "", page: 1 } : { list_search: "", page: 1 };
    if (clearFilter) {
      clearParams.list_filter = "";
      if (key) {
        switch (key) {
          case constant.TAGGED_WITH:
            return [setTaggedWith(""), baseHelper.setUrl(history, clearParams)];
          case constant.PRODUCT:
            return [
              setSelectedOptions(""),
              baseHelper.setUrl(
                history,
                { list_search: "", list_filter: "", page: 1 },
                setSearch(""),
                setSelectedFilter("")
              ),
            ];
          case constant.FILTER_BY_STATUS:
            return [setFilterByStatus(null), baseHelper.setUrl(history, clearParams)];
          case constant.CLEAR_ALL:
            return [
              setTaggedWith(""),
              setSelectedFilter(""),
              setFilterByStatus(null),
              setSearch(""),
              baseHelper.setUrl(history, clearParams),
            ];
          default:
            return null;
        }
      }
    }
    if (key === constant.FILTER_BY_STATUS) {
      setFilterByStatus(null);
    } else {
      clearParams.list_filter = "";
      setTaggedWith("");
      setSelectedFilter("");
    }
    baseHelper.setUrl(history, clearParams);
    return null;
  };

  let tagMarkup = [];
  if (filterByStatus && filterByStatus.length) {
    tagMarkup =
      filterByStatus &&
      filterByStatus.map((option) => (
        <Tag key={option} onRemove={removeTag(option)}>
          {baseHelper.ucFirst(option)}
        </Tag>
      ));
  }

  let productNameTag = [];
  if (selectedOptions && selectedOptions.length) {
    const fData = [...new Set(selectedOptions)];
    productNameTag =
      fData &&
      fData.map((option) => {
        const pData = (productList || []).find((item) => item.value === option);
        return (
          <Tag key={option} onRemove={removeTag(option, constant.PRODUCT)}>
            {baseHelper.ucFirst(pData.label || option)}
          </Tag>
        );
      });
  }

  /* const getSecondaryComponent = () => (
    <> 
      <QuickAction link={linkData} />
      <QuickReport />
    </>
  ); */

  const historyCollapseAction = (id) => {
    setBulkToggle((prev) => {
      return {
        ...prev,
        [id]: !bulkToggle[id],
      };
    });
    setExpanded((prev) => {
      return {
        ...prev,
        [id]: !expanded[id],
      };
    });
  };

  const toggleModal = (shopifyLineItemId = false) => {
    setInitiateReturn((prevState) => ({
      ...prevState,
      showModal: !prevState.showModal,
      selectedOrder: prevState.showModal ? false : shopifyLineItemId,
    }));
    setDisable(false);
  };

  const getTabs = {
    0: "all",
    1: "new",
    2: "accepted",
    3: "partial",
    4: "rejected",
    5: "shipped",
    6: "closed",
    7: "return",
  };
  const handleSelect = (item) => {
    setSelectedItems(item);
  };

  useEffect(() => {
    subscribeToMore({
      document: ORDER_LIST,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        refetch();
        return null;
      },
    });
  }, [subscribeToMore, refetch]);
  const pageContent = {
    label: {
      status: cms("section.orderList.label.status"),
      fulfillmentStatus: cms("section.orderList.label.fulfillmentStatus"),
    },
  };

  const renderPopUp = (shopifyLineItemId, item) => {
    const { status } = item;
    if (status === REJECT) {
      toggleModal(shopifyLineItemId);
    }
    return true;
  };

  const showReturnStatus = (lineReturnStatus, item) => {
    const { shopifyLineItemId, return: orderReturn, actualQuantity, quantity, returnedQuantity, isReturn } = item;
    const buttonShown = (
      <Stack.Item>
        <Button
          outline
          size="slim"
          destructive={false}
          onClick={() => renderPopUp(shopifyLineItemId, orderReturn)}
          key={`resourceListOrder${shopifyLineItemId}`}
        >
          {lineReturnStatus}
        </Button>
      </Stack.Item>
    );
    const { status } = orderReturn;

    const badgeShown =
      returnedQuantity < (actualQuantity || quantity) && orderReturn && status === ACCEPT && isReturn ? (
        <Badge status={baseHelper.getBadgeType(constant.RETURN)}>
          {statusHelper.getBadgeStatus(constant.PARTIAL_RETURN)}
        </Badge>
      ) : (
        <Badge status={baseHelper.getBadgeType(orderReturn.status)}>{baseHelper.ucFirst(lineReturnStatus)}</Badge>
      );

    if (status === CREATE || status === ACCEPT || status === ACKNOWLEDGE) {
      return badgeShown;
    }
    return buttonShown;
  };

  useEffect(() => {
    if (dataUpdateOrderLineTracking) {
      const updateOrderTracking = baseHelper.getResponseData(
        dataUpdateOrderLineTracking,
        constant.gql.UPDATE_ORDER_LINE_TRACKING
      );
      const updateOrderTrackingError = baseHelper.getResponseError(
        dataUpdateOrderLineTracking,
        constant.gql.UPDATE_ORDER_LINE_TRACKING
      );

      if (updateOrderTracking) {
        setTrackingModal(false);
        setInitialTrackingCompany("");
        setInitialTrackingNumber("");
        setBanner({
          status: constant.SUCCESS,
          title: cms("message.success.orderFulfilled"),
          isOpen: true,
        });
        refetch();
      }
      if (updateOrderTrackingError) {
        setTrackingModal(false);
        setBanner({
          status: constant.CRITICAL,
          title: updateOrderTrackingError,
          isOpen: true,
        });
      }
    }
  }, [dataUpdateOrderLineTracking, cms, refetch]);

  const promotedBulkActions = [
    {
      content: "Bulk Action",
    },
  ];

  const bulkActionButton = (key) => {
    let disabled = true;
    if (selectedItems && selectedItems.length) {
      selectedItems.forEach((orderId) => {
        const index = orderList.findIndex((orders) => {
          const { _id: id } = orders;
          return id === orderId;
        });
        if (
          key === constant.ACCEPT &&
          orderList[index] &&
          (orderList[index].status === constant.NEW || orderList[index].status === constant.PARTIAL)
        ) {
          disabled = false;
        }
        if (
          key === constant.REJECT &&
          orderList[index] &&
          (orderList[index].status === constant.NEW ||
            orderList[index].status === constant.ACCEPTED ||
            orderList[index].status === constant.PARTIAL)
        ) {
          disabled = false;
        }

        if (
          key === constant.FULFILLED &&
          orderList[index] &&
          [constant.ACCEPTED, constant.PARTIAL].includes(orderList[index].status)
        ) {
          disabled = false;
        }
      });
    }
    return disabled;
  };

  const bulkRejectAction = async () => {
    setChecked(false);
    const orderIds = isOrderReject ? [isOrderReject] : [...selectedItems];
    const refundData = [];
    setRejectModal(false);
    orderIds.forEach((orderId) => {
      orderList.map((item) => {
        if (orderId === item._id) {
          const orderLineIds =
            item.orderLines &&
            item.orderLines.map((val) => {
              const dataToAdd = {
                isAddToInventory: item.isAddToInventory || false,
                orderId: item._id,
                orderLineItemId: val._id,
              };
              refundData.push(dataToAdd);
              return false;
            });
          return orderLineIds;
        }
        return null;
      });
    });

    let formData = {};
    formData = {
      isRefund: false,
      refund: refundData,
    };
    await bulkReject({ variables: { input: { ...formData } } })
      .then((res) => {
        setSelectedItems([]);
        if (res) {
          const resData = baseHelper.getResponseData(res.data, constant.gql.BULK_REJECT);
          const responseDataError = baseHelper.getResponseError(res.data, constant.gql.BULK_REJECT);
          if (resData) {
            setMessage(
              `Order line rejected successfully,
               Please manage the transaction for this amount from your Shopify account manually.`
            );
            setBanner({ isOpen: false, title: "", status: constant.CRITICAL });
          }
          if (responseDataError) {
            setBanner({ isOpen: true, title: responseDataError, status: constant.CRITICAL });
          }
        }
      })
      .catch((exception) => {
        setBanner({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
      });
  };

  const checkDisabled = () => {
    let check = true;
    orderList.map((item) => {
      if (item._id === deliveryOrderId) {
        item.orderLines.map((element) => {
          let isReturnInitatied = false;
          const {
            actualQuantity,
            isDelivered,
            fulfillmentType,
            fulfillmentStatus,
            quantity,
            return: orderReturn,
            returnedQuantity,
          } = element;
          if (orderReturn && orderReturn.status) {
            isReturnInitatied = true;
          }

          if (
            element &&
            !isDelivered &&
            (!isReturnInitatied ||
              (returnedQuantity < (quantity || actualQuantity) &&
                orderReturn &&
                orderReturn.status === constant.ACCEPT)) &&
            [constant.FULFILLED, constant.SHIPPED_BY_VENDOR, constant.READY_TO_PICK].includes(fulfillmentStatus) &&
            [constant.DROPSHIP, constant.CONSIGNMENT, constant.PICKUP, constant.HYBRID].includes(fulfillmentType)
          ) {
            check = false;
            return check;
          }
        });
      }
    });
    return check;
  };

  const updateOrderDeliveryAction = async () => {
    const formData = { orderLineArray: [] };

    orderList.map((item) => {
      if (item._id === deliveryOrderId) {
        item.orderLines.map((element) => {
          let isReturnInitatied = false;
          const {
            actualQuantity,
            isDelivered,
            fulfillmentType,
            fulfillmentStatus,
            quantity,
            return: orderReturn,
            returnedQuantity,
          } = element;
          if (orderReturn && orderReturn.status) {
            isReturnInitatied = true;
          }

          if (
            element &&
            !isDelivered &&
            (!isReturnInitatied ||
              (returnedQuantity < (quantity || actualQuantity) &&
                orderReturn &&
                orderReturn.status === constant.ACCEPT)) &&
            [constant.FULFILLED, constant.SHIPPED_BY_VENDOR, constant.READY_TO_PICK].includes(fulfillmentStatus) &&
            [constant.DROPSHIP, constant.CONSIGNMENT, constant.PICKUP, constant.HYBRID].includes(fulfillmentType)
          ) {
            formData.orderLineArray.push({
              id: element._id,
            });
          }
        });
      }
    });

    await updateOrderDelivery({ variables: { input: { ...formData } } })
      .then((res) => {
        setSelectedItems([]);
        if (res) {
          const resData = baseHelper.getResponseData(res.data, constant.gql.UPDATE_ORDER_LINE_DELIVERY_STATUS);
          const responseDataError = baseHelper.getResponseError(
            res.data,
            constant.gql.UPDATE_ORDER_LINE_DELIVERY_STATUS
          );
          if (resData) {
            setMessage(cms("message.success.markDeliver"));
            setBanner({ isOpen: false, title: "", status: constant.CRITICAL });
          }
          if (responseDataError) {
            setBanner({ isOpen: true, title: responseDataError, status: constant.CRITICAL });
          }
        }
      })
      .catch((exception) => {
        setBanner({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
      });
  };

  const handlePageChange = (value) => {
    setSelectedItems([]);
    setPage(value);
  };

  const bulkAction = async () => {
    const orderIds = isOrderId ? [isOrderId] : [...selectedItems];
    let formData = {};
    const lineItemIds = [];
    orderIds.forEach((orderId) => {
      let orderLineItemIds;
      orderList.map((item) => {
        if (orderId === item._id) {
          orderLineItemIds =
            item &&
            item.orderLines.map((value) => {
              return value._id;
            });
        }
        return lineItemIds;
      });
      lineItemIds.push(...orderLineItemIds);
    });
    formData = {
      lineItemIds,
    };
    await bulkAccept({ variables: { input: { ...formData } } })
      .then((res) => {
        setSelectedItems([]);
        if (res) {
          const resData = baseHelper.getResponseData(res.data, constant.gql.BULK_ACCEPT);
          const responseDataError = baseHelper.getResponseError(res.data, constant.gql.BULK_ACCEPT);
          if (resData) {
            setMessage("Order line accepted successfully.");
          }
          if (responseDataError) {
            setBanner({ isOpen: true, title: responseDataError, status: constant.CRITICAL });
          }
        }
      })
      .catch((exception) => {
        setBanner({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
      });
  };

  const handleChecked = useCallback((newChecked) => setChecked(newChecked), []);

  const actions = () => (
    <Stack alignment="center">
      <Stack.Item fill>
        <Checkbox label={cms("label.checkbox")} checked={checked} onChange={handleChecked} />
      </Stack.Item>
      <Stack.Item>
        <Button
          onClick={(event) => {
            handleChange(event);
            handleChecked(!event);
          }}
        >
          {cms("common.button.cancel")}
        </Button>
      </Stack.Item>
      <Stack.Item>
        <Button primary onClick={bulkRejectAction} disabled={!checked}>
          {cms("common.button.confirm")}
        </Button>
      </Stack.Item>
    </Stack>
  );

  const renderReject = () => {
    return (
      <Modal open={rejectModel} onClose={handleChange} title={cms("modal.title")} footer={actions()}>
        <Modal.Section>
          <p>{cms("modal.description")}</p>
        </Modal.Section>
      </Modal>
    );
  };

  const bulkActions = [
    {
      content: "Bulk Accept",
      onAction: () => bulkAction(constant.ACCEPT),
      icon: CircleTickOutlineMinor,
      disabled:
        bulkActionButton(constant.ACCEPT) ||
        (getTabs[selectedTab] !== constant.PENDING.toLowerCase() &&
          getTabs[selectedTab] !== constant.ALL.toLowerCase() &&
          getTabs[selectedTab] !== constant.NEW.toLowerCase()),
    },
    {
      content: "Bulk Reject",
      onAction: () => setRejectModal(true),
      icon: CircleCancelMinor,
      destructive: true,
      disabled:
        bulkActionButton(constant.REJECT) ||
        (getTabs[selectedTab] !== constant.PENDING.toLowerCase() &&
          getTabs[selectedTab] !== constant.ALL.toLowerCase() &&
          getTabs[selectedTab] !== constant.ACCEPTED.toLowerCase() &&
          getTabs[selectedTab] !== constant.NEW.toLowerCase()),
    },
  ];
  if (isProductList) {
    bulkActions.push({
      content: "Bulk Fulfill",
      onAction: () => setIsFulfillModal(true),
      icon: MarkFulfilledMinor,
      disabled:
        bulkActionButton(constant.FULFILLED) ||
        (getTabs[selectedTab] !== constant.PENDING.toLowerCase() &&
          getTabs[selectedTab] !== constant.ALL.toLowerCase() &&
          getTabs[selectedTab] !== constant.NEW.toLowerCase()),
    });
  }
  const renderItem = (item) => {
    const {
      _id: id,
      actualQuantity,
      createdAt,
      financialStatus = constant.financialStatus.PENDING,
      image,
      isAutoExpiry,
      isAutoReject,
      isConsignmentPick,
      isRefund,
      isVendorFulfilled,
      orderNumber,
      quantity,
      rejectedByVendor = false,
      return: orderReturn,
      vendorCount,
      orderLines,
      shopifyOrderDate,
    } = item;
    // const [shopifyLineId] = orderLines;
    // const { shopifyLineItemId } = shopifyLineId;
    let { fulfillmentStatus: orderFulfillmentStatus, status } = item;
    const fulfillment = [];
    const orderLineStatus = [];
    const deliveredStatus = [];
    const isLineOrderReturn = [];
    const orderFulfillmentType = [constant.DROPSHIP, constant.CONSIGNMENT, constant.PICKUP, constant.HYBRID];
    const orderStatus = [constant.FULFILLED, constant.PARTIALLY_FULFILLED, constant.SHIPPED, constant.PARTIALLY_REFUND];
    let returnOrder = false;
    let returnedStatus = "";
    let productDeleted = false;

    orderLines.forEach((line) => {
      const {
        actualQuantity: actualLineQuantity,
        fulfillmentType = constant.DROPSHIP,
        fulfillmentStatus: lineFulfillmentStatus,
        isReturn = false,
        quantity: lineQuantity,
        status: lineStatus = "",
        isDelivered = false,
        isProductDeleted = false,
        return: returnedOrder,
        returnedQuantity,
      } = line;
      productDeleted = isProductDeleted;
      if (returnedQuantity < (lineQuantity || actualLineQuantity) && returnedOrder && returnedOrder.status) {
        returnedStatus = returnedOrder.status;
      }
      if (
        returnedQuantity < (actualLineQuantity || actualLineQuantity) &&
        returnedOrder &&
        returnedOrder.status === constant.ACCEPT
      ) {
        isLineOrderReturn.push(false);
      } else if (returnedOrder && returnedOrder.status === constant.REJECT) {
        isLineOrderReturn.push(true);
      } else if (lineFulfillmentStatus === constant.FULFILLED && !isDelivered) {
        isLineOrderReturn.push(isReturn);
      }
      fulfillment.push(fulfillmentType);
      if (
        !isDelivered &&
        lineStatus === constant.CLOSED &&
        [constant.CONSIGNMENT, constant.PICKUP].includes(fulfillmentType)
      ) {
        status = SHIPPED;
      }
      if (
        !isDelivered &&
        !isReturn &&
        returnedStatus !== constant.REJECT &&
        orderStatus.includes(orderFulfillmentStatus) &&
        lineFulfillmentStatus === constant.FULFILLED &&
        orderFulfillmentType.includes(fulfillmentType)
      ) {
        orderLineStatus.push(lineStatus);
      }
      deliveredStatus.push(!!isDelivered);
      returnOrder = isReturn;
    });

    const isReserve = [SHIPPED, CLOSED].includes(status) && fulfillment.includes(RESERVE);
    const isCollect = [SHIPPED, CLOSED].includes(status) && fulfillment.includes(COLLECT);
    const isDropship = [SHIPPED, CLOSED].includes(status) && fulfillment.includes(DROPSHIP);

    if (isDropship && !returnOrder && filterByStatus && !filterByStatus.length) {
      status = SHIPPED;
    }

    let fulfillmentStatus;
    fulfillment.forEach((fulfilType) => {
      fulfillmentStatus =
        [constant.PICKUP, constant.CONSIGNMENT].includes(fulfilType) && isVendorFulfilled
          ? (isConsignmentPick && cms("section.orderList.label.ready")) || cms("section.orderList.label.ship")
          : orderFulfillmentStatus;
    });

    if (fulfillmentStatus === PARTIALLY_FULFILLED) {
      fulfillmentStatus = FULFILLED;
    }

    if (filterByStatus && !filterByStatus.length && (isReserve || isCollect)) {
      status = COLLECTED;
    }

    let statusType;
    let reserveOrCollect;
    fulfillment.forEach((fulfilType) => {
      statusType = status === ACCEPTED && [RESERVE, COLLECT].includes(fulfilType) && fulfillmentStatus === PARTIAL;
      reserveOrCollect = [RESERVE, COLLECT].includes(fulfilType);
    });

    if (statusType && filterByStatus && !filterByStatus.length) {
      status = PARTIAL_COLLECT;
    }
    if (status === constant.REJECTED) {
      orderFulfillmentStatus = REFUND;
    }

    if (financialStatus === REFUND && reserveOrCollect) {
      status = constant.REJECTED;
    }

    const isPartialRefund = isRefund && orderFulfillmentStatus === REFUND && !!(actualQuantity || quantity);

    if (isPartialRefund) {
      orderFulfillmentStatus = PARTIAL_REFUND;
    }

    let isReturnInitatied = false;
    let lineReturnStatus = "";
    const isReturn = orderReturn && Object.keys(orderReturn).length;
    if (isReturn && orderReturn.status) {
      const { status: orderReturnStatus } = orderReturn;
      isReturnInitatied = true;
      lineReturnStatus = listData(cms).lineReturnStatus[orderReturnStatus];
    }
    const size = constant.LARGE;
    const thumbnailImage = image
      ? imageHelper.resize({ url: image, type: constant.imageTypes.THUMBNAIL })
      : constant.NOIMAGESNAP;
    // eslint-disable-next-line no-unused-vars
    const thumbnail = <Thumbnail source={thumbnailImage} size={size} />;

    const handleRejectModal = (rejectOrderId) => {
      setIsOrderReject(rejectOrderId);
    };

    const manageBulk = (field) => {
      let disabled = true;
      if (isOrderId) {
        orderList.map((orderListElement) => {
          if (
            isOrderId === orderListElement._id &&
            (orderListElement.status === constant.NEW || orderListElement.status === constant.PARTIAL)
          ) {
            disabled = false;
          }
          if (
            field === constant.REJECT &&
            isOrderId === orderListElement._id &&
            orderListElement.status !== constant.REJECTED &&
            orderListElement.status !== constant.CLOSED
          ) {
            disabled = false;
          }
          return disabled;
        });
      }
      return disabled;
    };

    const option = [
      {
        content: constant.VIEW_LABEL,
        icon: ViewMajor,
        onAction: () => {
          history.push(`${mainLink.orderNumber}${id}`);
        },
      },
      {
        content: ACCEPT_LABEL,
        onAction: () => bulkAction(constant.ACCEPT),
        icon: CircleTickOutlineMinor,
        disabled:
          manageBulk(constant.ACCEPT) ||
          (getTabs[selectedTab] !== constant.PENDING.toLowerCase() &&
            getTabs[selectedTab] !== constant.ALL.toLowerCase() &&
            getTabs[selectedTab] !== constant.NEW.toLowerCase()),
      },
      {
        content: REJECT_LABEL,
        onAction: () => {
          handleRejectModal();
          setRejectModal(true);
        },
        icon: CircleCancelMinor,
        destructive: true,
        disabled:
          manageBulk(constant.REJECT) ||
          (getTabs[selectedTab] !== constant.PENDING.toLowerCase() &&
            getTabs[selectedTab] !== constant.ALL.toLowerCase() &&
            getTabs[selectedTab] !== constant.ACCEPTED.toLowerCase() &&
            getTabs[selectedTab] !== constant.NEW.toLowerCase()),
      },
      {
        content: cms("section.orderList.button.markedAsDelivered"),
        onAction: () => {
          updateOrderDeliveryAction();
        },
        icon: WholesaleMajor,
        destructive: false,
        disabled: checkDisabled(),
      },
    ];

    const handlePopover = (orderId) => {
      if (popoverDisable) {
        return setActivePopover(false);
      }
      setSelectedItems([]);
      setGetOrderId(!getOrderId);
      setActivePopover({ [orderId]: !activePopover[orderId] });
      setIsOrderId(activePopover[orderId] ? "" : orderId);
      handleRejectModal(orderId);
      setDeliveryOrderId(orderId);
    };

    const financialStatusBadge = (financialStatus === constant.PAID && constant.PAID) || constant.CRITICAL;
    return (
      <ResourceItem id={id} persistActions>
        <ProductCard className={`product-card ${expanded[id] ? "Polaris-ResourceItem--selected" : ""}`}>
          <div className="list-item">
            <Stack alignment="leading">
              {/* <Stack.Item>{thumbnail}</Stack.Item> */}
              <Stack.Item fill>
                <div className="ellipsis">
                  <Link removeUnderline onClick={() => history.push(`${mainLink.orderNumber}${id}`)}>
                    {`Order #${orderNumber}`}
                  </Link>
                  {/* <Caption>
                    <TextStyle variation="subdued">
                      {` ${cms("section.orderList.label.lineItemId")}: ${shopifyLineItemId}`}
                    </TextStyle>
                  </Caption> */}
                  <Stack>
                    <Stack.Item>
                      <Caption>
                        <TextStyle variation="subdued">
                          {` ${cms("section.orderList.label.createdOn")}: ${baseHelper.formatDate(createdAt ? createdAt : shopifyOrderDate)}`}
                        </TextStyle>
                      </Caption>
                    </Stack.Item>
                    <Stack.Item>
                      <div className="history-items-versioning">
                        <Button
                          plain
                          disclosure={bulkToggle[id] ? "up" : "down"}
                          onClick={() => historyCollapseAction(id)}
                        >
                          <p className="content">{cms("section.orderVersion.button.history")}</p>
                        </Button>
                      </div>
                    </Stack.Item>
                  </Stack>
                  {/* <Caption>
                    {`${cms("section.orderList.label.vendor")}: `}
                    <Link onClick={() => history.push(`${links.vendor.link}${vendorId}`)}>{vendor}</Link>
                  </Caption>
                  <Caption>
                    {`${cms("section.orderList.label.lineItemId")}: `}
                    <span>{`#${shopifyLineItemId}`}</span>
                  </Caption> */}
                  <div>
                    <Stack>
                      {!deliveredStatus.includes(false)
                        ? !isRefund &&
                          !isReturn &&
                          !isAutoExpiry && (
                            <Stack.Item>
                              <Tooltip content={pageContent.label.status} preferredPosition="above">
                                <Badge status={baseHelper.getBadgeType(status)}>{constant.DELEVERED}</Badge>
                              </Tooltip>
                            </Stack.Item>
                          )
                        : !isRefund &&
                          !isReturn &&
                          !isAutoExpiry && (
                            <Stack.Item>
                              <Tooltip content={pageContent.label.status} preferredPosition="above">
                                <Badge status={baseHelper.getBadgeType(status)}>{baseHelper.ucFirst(status)}</Badge>
                              </Tooltip>
                            </Stack.Item>
                          )}
                      {isRefund && isAutoReject && (
                        <Stack.Item>
                          <Tooltip content={pageContent.label.status} preferredPosition="above">
                            <Badge status={baseHelper.getBadgeType(status)}>
                              {baseHelper.ucFirst(constant.AUTO_REJECT)}
                            </Badge>
                          </Tooltip>
                        </Stack.Item>
                      )}
                      {isAutoExpiry && (
                        <Stack.Item>
                          <Tooltip content={pageContent.label.status} preferredPosition="above">
                            <Badge status={baseHelper.getBadgeType(status)}>
                              {baseHelper.ucFirst(constant.NO_SHOW)}
                            </Badge>
                          </Tooltip>
                        </Stack.Item>
                      )}

                      {isRefund ? (
                        <Stack.Item>
                          <Tooltip content={pageContent.label.fulfillmentStatus} preferredPosition="above">
                            <Badge status={baseHelper.getBadgeType(orderFulfillmentStatus)}>
                              {baseHelper.ucFirst(orderFulfillmentStatus)}
                            </Badge>
                          </Tooltip>
                        </Stack.Item>
                      ) : (
                        <Stack.Item>
                          <Tooltip content={pageContent.label.fulfillmentStatus} preferredPosition="above">
                            <Badge
                              progress={orderFulfillmentStatus === PARTIALLY_FULFILLED ? PARTIALLY_COMPLETE : ""}
                              status={baseHelper.getBadgeType(fulfillmentStatus)}
                            >
                              {baseHelper.ucFirst(fulfillmentStatus)}
                            </Badge>
                          </Tooltip>
                        </Stack.Item>
                      )}
                      {financialStatus && (
                        <Stack.Item>
                          <Tooltip content={pageContent.label.status} preferredPosition="above">
                            <Badge status={baseHelper.getBadgeType(financialStatusBadge)}>
                              {baseHelper.getFinancialStatus(financialStatus)}
                            </Badge>
                          </Tooltip>
                        </Stack.Item>
                      )}
                      {rejectedByVendor && (
                        <Stack.Item>
                          <Tooltip content="Rejected By Vendor" preferredPosition="above">
                            <Badge status={constant.CRITICAL}>{productDeleted ? "Product deleted by vendor" : "Rejected by vendor"}</Badge>
                          </Tooltip>
                        </Stack.Item>
                      )}
                      <Stack.Item>
                        {fulfillmentStatus === FULFILLED &&
                          isReturnInitatied &&
                          showReturnStatus(lineReturnStatus, item)}
                      </Stack.Item>
                    </Stack>
                  </div>
                </div>
              </Stack.Item>
              <Stack.Item>
                <div className="manage-button">
                  <Popover
                    active={activePopover[id]}
                    setActive={() => {
                      handlePopover(id);
                    }}
                    options={option}
                  />
                </div>
              </Stack.Item>
            </Stack>
          </div>
          <StyledOrderVersioning>
            <div className="itemcollapse">
              <Collapsible
                open={bulkToggle && bulkToggle[id]}
                id="timeline"
                transition={{ duration: "150ms", timingFunction: "ease" }}
              >
                <OrderVersioning cms={cms} orderId={id} />
              </Collapsible>
              <div className="history-items-after-version">
                <Button
                  plain
                  removeUnderline
                  disclosure={active[id] ? "up" : "down"}
                  onClick={() => itemCollapseAction(id)}
                >
                  {`${active[id] ? HIDE_CAPITAL : SHOW} ${orderLines.length} ${
                    orderLines.length > 1 ? "items" : "item"
                  } from ${vendorCount} ${vendorCount > 1 ? "vendors" : "vendor"}`}
                </Button>
              </div>
            </div>
          </StyledOrderVersioning>
          <Collapsible
            open={active[id]}
            id={`timeline-${active[id] + 1}`}
            transition={{ duration: "150ms", timingFunction: "ease" }}
          >
            <ProductSubCard className="collapsible-card">
              <LineItem
                active={active}
                setActive={setActive}
                cms={cms}
                initialTrackingCompany={initialTrackingCompany}
                initialTrackingNumber={initialTrackingNumber}
                loadingOrderLineTracking={loadingOrderLineTracking}
                moneyFormat={moneyFormat}
                option={option}
                orderId={id}
                role={name}
                setBanner={setBanner}
                getOrderData={getOrderId}
                setInitialTrackingCompany={setInitialTrackingCompany}
                setInitialTrackingNumber={setInitialTrackingNumber}
                setMessage={setMessage}
                setTotalOrderItem={setTotalOrderItem}
                setTotalOrderVendor={setTotalOrderVendor}
                setTrackingModal={setTrackingModal}
                showHistory
                totalOrderItem={totalOrderItem}
                totalOrderVendor={totalOrderVendor}
                trackingModal={trackingModal}
                historyActive={historyActive}
                setHistoryActive={setHistoryActive}
                expanded={expanded}
                setExpanded={setExpanded}
                updateOrderLine={updateOrderLine}
                id={id}
                disable={disable}
                initiateReturn={initiateReturn}
                setDisable={setDisable}
                toggleModal={toggleModal}
                isEnabledData={isEnabledData}
              />
            </ProductSubCard>
          </Collapsible>
        </ProductCard>
      </ResourceItem>
    );
  };

  const handleSelectedFilterRemove = () => {
    setSelectedFilter("");
    setTaggedWith("");
    setSelectedOptions("");
    setSearchInput("");
    baseHelper.setUrl(history, { list_search: "", list_filter: "", page: 1 });
  };

  const handleClearAll = () => {
    handleTaggedWithRemove(true, constant.CLEAR_ALL);
    handleSelectedFilterRemove();
    handleQueryClear();
    setFilterByStatus(null);
  };

  function disambiguateLabel(key, value) {
    switch (key) {
      case constant.TAGGED_WITH:
        return `${value}`;
      case constant.SELECTED_FILTER:
        return value === constant.PRODUCT ? (
          <div className="remove_tag">{productNameTag}</div>
        ) : (
          cms(`section.orderList.label.filter.${value}`) || value
        );

      case constant.FILTER_BY_STATUS:
        return <div className="remove_tag">{tagMarkup}</div>;
      default:
        return value;
    }
  }

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === "" || value == null;
  }
  const appliedFilterArrObj = [];
  if (!isEmpty(taggedWith)) {
    appliedFilterArrObj.push({
      key: constant.TAGGED_WITH,
      label: disambiguateLabel(constant.TAGGED_WITH, taggedWith),
      onRemove: () => handleTaggedWithRemove(false),
    });
  }
  if (!isEmpty(selectedFilter)) {
    appliedFilterArrObj.push({
      key: constant.SELECTED_FILTER,
      label: disambiguateLabel(constant.SELECTED_FILTER, selectedFilter),
      onRemove: () => handleTaggedWithRemove(false, selectedFilter),
    });
  }

  if (!isEmpty(filterByStatus)) {
    appliedFilterArrObj.push({
      key: constant.FILTER_BY_STATUS,
      label: disambiguateLabel(constant.FILTER_BY_STATUS, filterByStatus),
      onRemove: () => handleTaggedWithRemove(false, constant.FILTER_BY_STATUS),
    });
  }

  const handleAction = (value) => {
    setFilterByStatus(value);
    baseHelper.setUrl(history, { filter: value, page: 1 });
    if (!value.length) {
      baseHelper.setUrl(history, { filter: "all", page: 1 });
    }
  };

  const appliedFilters = appliedFilterArrObj;
  const filters = [
    {
      key: "taggedWith",
      label: cms("section.orderList.label.filterBy"),
      filter: (
        <MoreFilters
          dataToFetch={inputData}
          loading={loading}
          productTag={productTag}
          queryParam={queryParam}
          selectedFilter={selectedFilter}
          selectedOptions={selectedOptions}
          setBanner={setBanner}
          setProductTag={setProductTag}
          setSelectedFilter={setSelectedFilter}
          setSelectedOptions={setSelectedOptions}
          setTaggedWith={setTaggedWith}
          taggedWith={taggedWith}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          setTagInputValue={setTagInputValue}
          productList={productList}
          setProductList={setProductList}
        />
      ),
      shortcut: false,
    },
    {
      key: constant.FILTER_BY_STATUS,
      label: cms("common.label.filterByStatus"),
      filter: (
        <ChoiceList
          title={cms("common.label.filterByStatus")}
          titleHidden
          choices={filterByStatusOption}
          selected={filterByStatus || []}
          onChange={(value) => handleAction(value)}
          allowMultiple
        />
      ),
    },
  ];

  const closeBanner = () => {
    setBanner({
      isOpen: false,
      status: "",
      title: "",
    });
  };

  if (
    loadingBulkAccept ||
    loadingBulkReject ||
    loadingOrderLineTracking ||
    loadingUpdateOrderDelivery ||
    loadingProductListing
  ) {
    return <Spinner />;
  }
  const handleClose = () => {
    // storageHelper.set("customAppUpdate", "No");
    storageHelper.set("customAppUpdate", "updated");
    setIsOpen(false);
  };

  const getComponent = () => {
    return (
      <>
        {!isShopifyCustomOperator && appUpdate === "updated" && (
          <>
            <Banner
              isOpen
              action={{ content: "Migrate to Custom App", onAction: () => setIsOpen(true) }}
              title={cms("common.popup.migrate.title")}
              status="critical"
            >
              <p>{cms("common.popup.migrate.description")}</p>
            </Banner>
            <ModalPopup open={isOpen} onClose={handleClose} title={cms("common.popup.migrate.banner.title")}>
              <Card>
                <TextContainer>
                  <Banner
                    isOpen
                    status="critical"
                    action={{
                      content: `${cms("common.appPermission.label.aboutCustom")}`,
                      url: constant.ABOUT_OPERATOR_APP_MIGRATE,
                    }}
                  >
                    <TextContainer>{cms("common.popup.migrate.description")}</TextContainer>
                  </Banner>
                </TextContainer>
              </Card>
              <UpgradAppPermission />
            </ModalPopup>
            <br />
          </>
        )}
        {/* {isShopifyCustomOperator && !fulfillmentScopeExists && <PermissionBanner />} */}
        <br />
        {renderReject()}
        <QuickReport />
        <QuickAction link={linkData} />
        <Layout>
          {banner.isOpen && (
            <>
              <Layout.Section>
                <Banner
                  isOpen={banner.isOpen}
                  status={banner.status}
                  title={banner.title}
                  action={banner.action}
                  onDismiss={() => closeBanner()}
                />
              </Layout.Section>
              <br />
            </>
          )}
          <OperatorExportOrder isOpenModal={isExportModal} setIsOpenModal={setIsExportModal} />
          <FulfillOrder
            cms={cms}
            isFulfillModal={isFulfillModal}
            setIsFulfillModal={setIsFulfillModal}
            setBanner={setBanner}
            orderList={orderList}
            selectedItems={selectedItems}
            history={history}
            isEnabledData={isEnabledData}
          />
          <SyncOrder
            cms={cms}
            isSyncOpen={isSyncModal}
            setIsSyncModal={setIsSyncModal}
            setBanner={setBanner}
            refetch={refetch}
          />
          <Layout.Section>
            <Card>
              <div className="order-list">
                <ResourceList
                  items={orderList}
                  renderItem={renderItem}
                  idForItem={(orders) => {
                    const { _id: id } = orders;
                    return id;
                  }}
                  loading={loading || orderLoading}
                  queryValue={search}
                  onQueryClear={handleQueryClear}
                  onQueryChange={handleSearchChange}
                  sortOptions={listData(cms).sortOptions}
                  sortValue={sortValue}
                  onSortChange={handleSort}
                  selectedItems={selectedItems}
                  onSelectionChange={handleSelect}
                  bulkActions={bulkActions}
                  promotedBulkActions={promotedBulkActions}
                  filters={filters}
                  appliedFilters={appliedFilters}
                  handleClearAll={handleClearAll}
                  count={orderCount}
                  page={page}
                  perPage={perPage}
                  setPage={(value) => handlePageChange(value)}
                  setPerPage={setPerPage}
                  setPopoverDisable={setPopoverDisable}
                  totalCountLabel={cms("section.orderList.label.totalOrder")}
                />
              </div>
            </Card>
            <Toast message={message} />
          </Layout.Section>
        </Layout>
      </>
    );
  };
  // <TwoColumnLayout primary={getComponent()} secondary={getSecondaryComponent() />
  return <SingleColumnLayout primary={getComponent()} />;
};

export default withFeature(withErrorBoundary(OperatorOrderList), { feature: constant.ORDERS });
